/**
 * Auto-match a scanned keg to a Taproom order line by product (and optional productId).
 * Used by the single Scan Kegs modal on step 3.
 */

import { isValidKegCode, normalizeKegCode } from './kegCode';
import {
  kegMatchesLine as sharedKegMatchesLine,
  matchKegToLine,
  packageSizeMismatch,
  productMatchesLine,
  type LineMatchSide,
} from './kegLineMatch';
import { isKegPackage } from './transferItems';

export interface TaproomScanLine {
  itemName: string;
  quantity: number;
  kegCodes?: string[];
  productId?: number | null;
  productName?: string | null;
  packageType?: string | null;
}

export interface KegLookup {
  code?: string;
  status?: string | null;
  productId?: number | null;
  productName?: string | null;
  locationId?: number | null;
  batchId?: string | number | null;
  batchCode?: string | null;
  packagingType?: string | null;
  packageType?: string | null;
}

export class TaproomKegScanError extends Error {
  code: 'INVALID' | 'NOT_FILLED' | 'WRONG_LOCATION' | 'DUPLICATE' | 'NOT_ON_ORDER' | 'WRONG_SIZE' | 'LINE_FULL';

  constructor(
    message: string,
    code: TaproomKegScanError['code']
  ) {
    super(message);
    this.name = 'TaproomKegScanError';
    this.code = code;
  }
}

/** All keg serials already assigned on the order (normalized). */
export function allAssignedKegCodes(lines: TaproomScanLine[]): string[] {
  const out: string[] = [];
  for (const line of lines) {
    for (const c of line.kegCodes || []) {
      out.push(normalizeKegCode(c));
    }
  }
  return out;
}

/** True when keg product + package size match this order line. */
export function kegMatchesLine(keg: KegLookup, line: TaproomScanLine): boolean {
  return sharedKegMatchesLine(keg, line as LineMatchSide);
}

/**
 * Resolve which order line a keg belongs to.
 * Prefers a matching line that still has open qty; if all matching lines are full, LINE_FULL.
 */
export function findLineForKeg(
  keg: KegLookup,
  lines: TaproomScanLine[]
): { lineIndex: number; styleLabel: string } {
  const matches: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (kegMatchesLine(keg, lines[i])) matches.push(i);
  }
  if (!matches.length) {
    const code = normalizeKegCode(keg.code || '');
    // Product matches a line but size is wrong → clear size error
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!isKegPackage(line)) continue;
      if (!productMatchesLine(keg, line)) continue;
      const sizeMis = packageSizeMismatch(keg, line);
      if (sizeMis) {
        throw new TaproomKegScanError(
          `Keg ${code} is ${sizeMis.kegSize}; this line is ${sizeMis.lineSize}`,
          'WRONG_SIZE'
        );
      }
    }
    const detail = matchKegToLine(keg, lines.find(isKegPackage) || lines[0] || { itemName: '' }, {
      kegCode: code,
    });
    const label = keg.productName || code || 'this keg';
    throw new TaproomKegScanError(
      detail.ok === false && detail.reason === 'PRODUCT'
        ? detail.message
        : `Keg ${code} is not on this order (${label})`,
      'NOT_ON_ORDER'
    );
  }
  for (const idx of matches) {
    const line = lines[idx];
    const need = Math.round(Number(line.quantity) || 0);
    const have = (line.kegCodes || []).length;
    if (have < need) {
      return { lineIndex: idx, styleLabel: line.itemName };
    }
  }
  const full = lines[matches[0]];
  throw new TaproomKegScanError(
    `Already scanned ${Math.round(Number(full.quantity) || 0)} of ${Math.round(
      Number(full.quantity) || 0
    )} for ${full.itemName}`,
    'LINE_FULL'
  );
}

/**
 * Validate keg + assign to the matching order line.
 * Returns a new lines array (immutable) with the keg appended.
 */
export function assignScannedKegToLines(
  codeRaw: string,
  keg: KegLookup,
  lines: TaproomScanLine[],
  opts: { fromLocationId?: number | null } = {}
): { lines: TaproomScanLine[]; lineIndex: number; code: string; styleLabel: string } {
  const code = normalizeKegCode(codeRaw || keg.code || '');
  if (!isValidKegCode(code)) {
    throw new TaproomKegScanError('Valid keg tag needed (e.g., EK09503)', 'INVALID');
  }

  const status = String(keg.status || '').toLowerCase();
  if (status !== 'filled') {
    throw new TaproomKegScanError(
      `Keg ${code} is not filled (status=${keg.status || 'unknown'})`,
      'NOT_FILLED'
    );
  }

  if (
    opts.fromLocationId != null &&
    keg.locationId != null &&
    Number(keg.locationId) !== Number(opts.fromLocationId)
  ) {
    throw new TaproomKegScanError(
      `Keg ${code} is not at the brewery cooler`,
      'WRONG_LOCATION'
    );
  }

  const assigned = allAssignedKegCodes(lines);
  if (assigned.includes(code)) {
    throw new TaproomKegScanError(`Keg ${code} is a duplicate (already scanned)`, 'DUPLICATE');
  }

  const { lineIndex, styleLabel } = findLineForKeg({ ...keg, code }, lines);
  const next = lines.map((l, i) =>
    i === lineIndex
      ? { ...l, kegCodes: [...(l.kegCodes || []), code] }
      : { ...l, kegCodes: [...(l.kegCodes || [])] }
  );
  return { lines: next, lineIndex, code, styleLabel };
}

export function removeKegFromLines(
  lines: TaproomScanLine[],
  codeRaw: string
): TaproomScanLine[] {
  const code = normalizeKegCode(codeRaw);
  return lines.map((l) => ({
    ...l,
    kegCodes: (l.kegCodes || []).filter((c) => normalizeKegCode(c) !== code),
  }));
}

/** Groups for the scan modal live list (keg package lines only). */
export function kegScanGroups(lines: TaproomScanLine[]) {
  return lines
    .filter(isKegPackage)
    .map((line) => {
      const ordered = Math.round(Number(line.quantity) || 0);
      const codes = (line.kegCodes || []).map((c) => normalizeKegCode(c));
      return {
        itemName: line.itemName,
        styleLabel: line.itemName,
        ordered,
        scanned: codes.length,
        codes,
        complete: codes.length === ordered && ordered > 0,
      };
    });
}
